import { useContext, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Languages,
  LogOut,
  Moon,
  ShieldCheck,
  Sun,
  UserRound,
  UsersRound,
} from "lucide-react";

import { AuthContext } from "../context/AuthContext";
import { LanguageContext } from "../context/LanguageContext";
import { useTheme } from "../context/ThemeContext";

const SettingsLink = ({ icon: Icon, title, subtitle, to }) => (
  <Link
    to={to}
    className="flex min-h-16 items-center gap-3 border-b border-app px-4 py-3 transition-colors last:border-b-0 hover:bg-app-elevated"
  >
    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-app-elevated text-app-accent">
      <Icon size={18} />
    </span>
    <span className="min-w-0 flex-1">
      <span className="block text-sm font-semibold text-app">{title}</span>
      <span className="block truncate text-xs text-app-muted">{subtitle}</span>
    </span>
    <ChevronRight size={17} className="shrink-0 text-app-muted" />
  </Link>
);

const SettingsPage = () => {
  const { user, logout, hasAnyPermission } = useContext(AuthContext);
  const { lang } = useContext(LanguageContext);
  const { theme, applyTheme } = useTheme();
  const [signingOut, setSigningOut] = useState(false);

  const canViewTeam = hasAnyPermission([
    "team.view",
    "team.update",
    "team.manage_permissions",
  ]);

  const handleLogout = async () => {
    setSigningOut(true);
    await logout();
  };

  return (
    <div className="page-shell mx-auto max-w-4xl space-y-7 px-1 py-2">
      <header className="border-b border-app pb-5">
        <p className="eyebrow">Workspace</p>
        <h1 className="mt-2 text-2xl font-semibold text-app">Settings</h1>
        <p className="mt-1 text-sm text-app-muted">
          {user?.email || "Account preferences"}
        </p>
      </header>

      <section aria-labelledby="appearance-heading">
        <h2 id="appearance-heading" className="mb-3 text-sm font-semibold text-app">
          Appearance
        </h2>
        <div className="overflow-hidden rounded-lg border border-app bg-app-surface">
          <div className="flex min-h-16 items-center justify-between gap-4 px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-app-elevated text-app-accent">
                {theme === "dark" ? <Moon size={18} /> : <Sun size={18} />}
              </span>
              <div>
                <p className="text-sm font-semibold text-app">Color theme</p>
                <p className="text-xs text-app-muted">Applied across the confirmation workspace</p>
              </div>
            </div>
            <div className="inline-flex shrink-0 rounded-lg border border-app bg-app-elevated p-1">
              <button
                type="button"
                aria-pressed={theme === "light"}
                onClick={() => applyTheme("light")}
                className={`grid h-8 w-8 place-items-center rounded-md transition-colors ${
                  theme === "light" ? "bg-white text-gray-900 shadow-sm" : "text-app-muted"
                }`}
                title="Light theme"
              >
                <Sun size={16} />
              </button>
              <button
                type="button"
                aria-pressed={theme === "dark"}
                onClick={() => applyTheme("dark")}
                className={`grid h-8 w-8 place-items-center rounded-md transition-colors ${
                  theme === "dark" ? "bg-gray-800 text-white shadow-sm" : "text-app-muted"
                }`}
                title="Dark theme"
              >
                <Moon size={16} />
              </button>
            </div>
          </div>

          <div className="flex min-h-16 items-center justify-between gap-4 border-t border-app px-4 py-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-app-elevated text-app-accent">
                <Languages size={18} />
              </span>
              <div>
                <p className="text-sm font-semibold text-app">Interface language</p>
                <p className="text-xs text-app-muted">English</p>
              </div>
            </div>
            <select
              value={lang}
              disabled
              aria-label="Interface language"
              className="field-input h-9 min-w-28 rounded-lg px-3 text-sm disabled:cursor-not-allowed disabled:opacity-70"
            >
              <option value="en">English</option>
            </select>
          </div>
        </div>
      </section>

      <section aria-labelledby="account-heading">
        <h2 id="account-heading" className="mb-3 text-sm font-semibold text-app">
          Account
        </h2>
        <div className="overflow-hidden rounded-lg border border-app bg-app-surface">
          <SettingsLink
            icon={UserRound}
            title="Profile"
            subtitle="Identity, contact details and public profile"
            to="/profile"
          />
          {canViewTeam && (
            <SettingsLink
              icon={UsersRound}
              title="Team and permissions"
              subtitle="Internal users and access rights"
              to="/team"
            />
          )}
          <SettingsLink
            icon={ShieldCheck}
            title="Collaboration access"
            subtitle="Active relationships and assignment rights"
            to="/collaborations"
          />
        </div>
      </section>

      <section aria-labelledby="session-heading">
        <h2 id="session-heading" className="mb-3 text-sm font-semibold text-app">
          Session
        </h2>
        <button
          type="button"
          onClick={handleLogout}
          disabled={signingOut}
          className="flex min-h-12 w-full items-center justify-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300"
        >
          <LogOut size={17} />
          {signingOut ? "Signing out..." : "Sign out"}
        </button>
      </section>
    </div>
  );
};

export default SettingsPage;
